import {defineStore, storeToRefs} from "pinia";
import type {BFilter, BFilterableField} from "@/components/BFilterEditor/BFilterEditor.types";
import axios from "axios";
import {ref} from "vue";
import {debounce} from "vue-debounce";
import {handleError} from "./storeHelper";
import type {AgencyStaffing, EmployeeData} from "@/types";
import {useGlobalMessagesStore} from "./globalMessagesStore";
import {useOriStore} from "./oriStore";
import {UploadType} from "@/types";

// Add/edit submits a Police Employee JSON document through the same multipart upload endpoint
// (api/v1/police-employee, PoliceEmployeeController.PostFormData) the legacy flat-file upload used -
// the JSON ingestion path there upserts by (ori, year), so create and update are the same wire call.
// Shape/field names per the VCO10/VSTD10 schemas in beyond2020.policeemployee
// (Impl/VCO10/schema.ts, Impl/VSTD10/schema.ts) - note "specification.version" is always the
// Blank form string -> null (not submitted), otherwise the number.
const optionalCount = (value: string | null | undefined): number | null => {
    const trimmed = (value ?? "").toString().trim();
    return trimmed === "" ? null : Number(trimmed);
};

// literal "1.0" on the wire for both flavors; "CO-1.0"/"STD-1.0" are internal tokens the backend
// derives from "specification.name" and never appear in the JSON itself.
function buildSubmissionDocument(stateCode: string, record: EmployeeData) {
    const isCO = stateCode == "CO";
    // The schemas require integers; the number inputs can hand back strings (e.g. "5"), which the
    // backend rejects (PE4014), so coerce every count explicitly.
    const num = (value: number | string | null | undefined) => Number(value ?? 0);

    const report: Record<string, number | string> = {
        ori: record.ori,
        year: num(record.year),
        officers_male: num(record.sworn_in_male),
        officers_female: num(record.sworn_in_female),
        civilians_male: num(record.civilian_male),
        civilians_female: num(record.civilian_female),
    };

    if (isCO) {
        // Optional in the CO-1.0 schema: a blank field is omitted (not submitted), never sent as 0.
        const optionalNum = (key: string, value: string | null | undefined) => {
            const trimmed = (value ?? "").toString().trim();
            if (trimmed !== "") report[key] = Number(trimmed);
        };
        optionalNum("certified_officers", record.certified_officers);
        optionalNum("noncertified_officers", record.non_certified_officers);
        optionalNum("lea_budget", record.lea_budget);
    }

    return {
        specification: {
            name: isCO ? "CO Police Employees" : "Police Employees",
            version: "1.0",
        },
        type: "Submission",
        submit_reports: [report],
    };
}

// Deletion goes through the same JSON submission endpoint as add/edit, as a "delete_reports" entry
// instead of "submit_reports" - the ingestion pipeline (PeIngestion.CreateDataItemJson) already
// handles DeleteDetails reports by writing a Delete-kind DataItem, which QueryLatestGoodItemsByOri
// then excludes from the "latest good items" the staffing grid reads from.
function buildDeletionDocument(stateCode: string, ori: string, year: number) {
    const isCO = stateCode == "CO";

    return {
        specification: {
            name: isCO ? "CO Police Employees" : "Police Employees",
            version: "1.0",
        },
        type: "Submission",
        delete_reports: [{ori, year}],
    };
}

function buildSubmissionFileName(ori: string, year: number): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;

    return `${ori}_${year}_PE_${timestamp}.json`;
}

export const useEmployeeDataStore = defineStore("employeeDataStore", () => {
    const globalMessages = useGlobalMessagesStore();
    const loading = ref<boolean>(false);
    const items = ref<AgencyStaffing[]>([]);

    // ori related functions & computed properties - the component never talks to the oriStore directly.
    // Pointing the oriStore at the PE dataset scopes its ori list and dataset details to Police Employee.
    const oriStore = useOriStore();
    oriStore.reset();
    oriStore.dataset = UploadType.policeEmployee;

    const getOriListForDataset = (filterInfo: BFilter[] = [], filterableFields: BFilterableField[] = []) =>
        oriStore.getOriListForDataset("policeEmployee", filterInfo, filterableFields);
    const fetchDatasetDetailsForOri = oriStore.fetchDatasetDetailsForOri;
    const {getDetailsForCurrentDataset} = storeToRefs(oriStore);

    const list = async (ori: string) => {
        try {
            loading.value = true;
            const response = await axios.get<AgencyStaffing[]>(`api/v1/ori/${ori}/staffing-all-years`);
            items.value = response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    // Reflects an add/edit/delete in the grid immediately instead of waiting on the processing queue.
    // Submission is async (a queued job, not a direct write), so the optimistic state could turn out to
    // be wrong if the queue later rejects it (e.g. a business rule) - reconcileOri
    // re-fetches from the server once, after everything's settled, to silently correct that if it happens.
    const upsertLocalItem = (record: EmployeeData) => {
        const updated: AgencyStaffing = {
            year: record.year,
            sworn_in_male: record.sworn_in_male,
            sworn_in_female: record.sworn_in_female,
            civilian_male: record.civilian_male,
            civilian_female: record.civilian_female,
            certified_officers: optionalCount(record.certified_officers),
            non_certified_officers: optionalCount(record.non_certified_officers),
            lea_budget: optionalCount(record.lea_budget),
        };

        const existingItems = items.value ?? (items.value = []);
        const index = existingItems.findIndex((it) => it.year === record.year);
        if (index >= 0) {
            // Preserve any document already associated with this record - editing the counts doesn't touch it.
            updated.file_id = existingItems[index].file_id;
            existingItems[index] = updated;
        } else {
            existingItems.push(updated);
        }
    };

    const removeLocalItem = (year: number) => {
        if (items.value) {
            items.value = items.value.filter((it) => it.year !== year);
        }
    };

    let pendingReconciliationOri = "";
    const reconcileOri = debounce(() => {
        if (pendingReconciliationOri) {
            list(pendingReconciliationOri);
        }
    }, "20000ms");

    const scheduleReconciliation = (ori: string) => {
        pendingReconciliationOri = ori;
        reconcileOri();
    };

    const submit = async (stateCode: string, record: EmployeeData): Promise<void> => {
        try {
            loading.value = true;

            const document = buildSubmissionDocument(stateCode, record);
            const blob = new Blob([JSON.stringify(document)], {type: "application/json"});

            const formData = new FormData();
            formData.append("files", blob, buildSubmissionFileName(record.ori, record.year));

            await axios.post("api/v1/police-employee", formData);

            upsertLocalItem(record);
            scheduleReconciliation(record.ori);

            globalMessages.push({
                message: "Employee data submitted successfully. It's been queued for processing - check the Data Upload page for status.",
                type: "success",
            });
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const create = (stateCode: string, record: EmployeeData) => submit(stateCode, record);
    const update = (stateCode: string, record: EmployeeData) => submit(stateCode, record);

    const uploadDocument = async (ori: string, year: number, file: File): Promise<number> => {
        try {
            loading.value = true;

            const formData = new FormData();
            formData.append("file", file, file.name);

            const response = await axios.post<{file_id: number}>(`api/v1/police-employee/ori/${ori}/year/${year}/document`, formData);

            globalMessages.push({message: "Document uploaded successfully.", type: "success"});

            return response.data.file_id;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const deleteDocument = async (ori: string, year: number): Promise<void> => {
        try {
            loading.value = true;

            await axios.delete(`api/v1/police-employee/ori/${ori}/year/${year}/document`);

            globalMessages.push({message: "Document deleted successfully.", type: "success"});
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const deleteItem = async (stateCode: string, ori: string, year: number): Promise<void> => {
        try {
            loading.value = true;

            const document = buildDeletionDocument(stateCode, ori, year);
            const blob = new Blob([JSON.stringify(document)], {type: "application/json"});

            const formData = new FormData();
            formData.append("files", blob, buildSubmissionFileName(ori, year));

            await axios.post("api/v1/police-employee", formData);

            removeLocalItem(year);
            scheduleReconciliation(ori);

            globalMessages.push({
                message: "Employee data deletion submitted successfully. It's been queued for processing - check the Data Upload page for status.",
                type: "success",
            });
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    return {
        items,
        loading,
        getOriListForDataset,
        fetchDatasetDetailsForOri,
        getDetailsForCurrentDataset,
        list,
        create,
        update,
        deleteItem,
        uploadDocument,
        deleteDocument,
    };
});
